import React, { useState, useEffect } from 'react';
import {
  Activity, Save, ChevronDown, ChevronUp, TrendingUp,
  DollarSign, Percent, Bell, Layers, Timer,
  CheckCircle2, XCircle, Loader2, Info, Zap, AlertTriangle,
} from 'lucide-react';
import { positionMonitorAPI } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';

// ── shared styles ─────────────────────────────────────────────────────────────
const fieldCls =
  'px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg ' +
  'text-xs focus:border-[var(--accent-primary)] focus:outline-none ' +
  'text-[var(--text-primary)] placeholder-[var(--text-tertiary)] w-full transition-colors';

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ active, onChange, disabled = false }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : () => onChange(!active)}
      className="relative rounded-full transition-all duration-300 shrink-0"
      style={{
        width: '2.25rem', height: '1.25rem',
        backgroundColor: active ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
        border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-color)'}`,
        boxShadow: active ? '0 0 8px color-mix(in srgb, var(--accent-primary) 35%, transparent)' : 'none',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <span
        className="absolute rounded-full bg-white shadow transition-all duration-300"
        style={{
          width: '0.875rem', height: '0.875rem',
          top: '50%', left: active ? 'calc(100% - 3px)' : '3px',
          transform: active ? 'translate(-100%, -50%)' : 'translate(0, -50%)',
        }}
      />
    </button>
  );
}

// ── Section card ──────────────────────────────────────────────────────────────
function Section({ icon: Icon, title, subtitle, color, enabled, onToggle, badge, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div
      className={`rounded-xl border overflow-hidden transition-all duration-200 ${enabled ? '' : 'opacity-70'}`}
      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: enabled ? color : 'var(--border-color)' }}
    >
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        onClick={() => setOpen(o => !o)}
        style={{ borderBottom: open && enabled ? '1px solid var(--border-color)' : 'none' }}
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-[var(--text-primary)]">{title}</p>
            {badge && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                style={{ backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)`, color }}>
                {badge}
              </span>
            )}
          </div>
          <p className="text-xs text-[var(--text-tertiary)]">{subtitle}</p>
        </div>
        <Toggle active={enabled} onChange={onToggle} />
        <button
          className="p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
          onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        >
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>
      {open && enabled && <div className="px-4 py-3 space-y-3">{children}</div>}
    </div>
  );
}

// ── Action row ────────────────────────────────────────────────────────────────
function ActionRow({ icon: Icon, label, sublabel, checked, onChange, color, children }) {
  return (
    <div
      className="rounded-lg border transition-all overflow-hidden"
      style={{
        backgroundColor: checked ? `color-mix(in srgb, ${color} 6%, transparent)` : 'var(--bg-tertiary)',
        borderColor: checked ? `color-mix(in srgb, ${color} 30%, transparent)` : 'var(--border-color)',
      }}
    >
      <div className="flex items-start gap-3 px-3 py-2.5 cursor-pointer" onClick={() => onChange(!checked)}>
        <div
          className="w-5 h-5 rounded flex items-center justify-center shrink-0 mt-0.5 transition-all"
          style={{
            backgroundColor: checked ? color : 'var(--bg-secondary)',
            border: `1.5px solid ${checked ? color : 'var(--border-color)'}`,
          }}
        >
          {checked && <Icon className="w-3 h-3 text-white" />}
        </div>
        <div className="flex-1">
          <p className="text-xs font-semibold text-[var(--text-primary)]">{label}</p>
          <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{sublabel}</p>
        </div>
      </div>
      {checked && children && (
        <div className="px-3 pb-3 pt-0 border-t" style={{ borderColor: `color-mix(in srgb, ${color} 20%, transparent)` }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── Stat pill ─────────────────────────────────────────────────────────────────
function StatPill({ label, val, color }) {
  return (
    <div className="px-3 py-2 rounded-lg border" style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)' }}>
      <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-0.5">{label}</p>
      <p className="text-sm font-bold" style={{ color }}>{val}</p>
    </div>
  );
}

// ── Interval slider ───────────────────────────────────────────────────────────
const INTERVAL_PRESETS = [
  { ms: 250,  label: '250ms', note: 'Aggressive' },
  { ms: 500,  label: '500ms', note: 'Default' },
  { ms: 1000, label: '1s',    note: 'Balanced' },
  { ms: 2000, label: '2s',    note: 'Light' },
  { ms: 5000, label: '5s',    note: 'Minimal' },
];

function IntervalPicker({ value, onChange }) {
  const current = INTERVAL_PRESETS.find(p => p.ms === value) || INTERVAL_PRESETS[1];
  return (
    <div className="space-y-2">
      <div className="flex gap-1.5 flex-wrap">
        {INTERVAL_PRESETS.map(p => (
          <button
            key={p.ms}
            onClick={() => onChange(p.ms)}
            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={{
              backgroundColor: value === p.ms ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
              color: value === p.ms ? '#fff' : 'var(--text-secondary)',
              border: `1px solid ${value === p.ms ? 'var(--accent-primary)' : 'var(--border-color)'}`,
            }}
          >
            {p.label}
            <span className="ml-1 text-[9px] opacity-70">{p.note}</span>
          </button>
        ))}
      </div>
      <p className="text-[10px] text-[var(--text-tertiary)]">
        Bot scans all open positions every <strong>{current.label}</strong>.
        Faster = more Redis load. 500ms is recommended for most brokers.
      </p>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN
// ═════════════════════════════════════════════════════════════════════════════
export default function PositionMonitorConfig() {
  const { t } = useLocale();

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(false);
  const [stats,   setStats]   = useState(null);

  const [cfg, setCfg] = useState({
    // triggers
    trigger_pct_balance_enabled:   false,
    trigger_pct_balance_threshold: '',
    trigger_x_margin_enabled:      false,
    trigger_x_margin_threshold:    '',
    trigger_absolute_enabled:      false,
    trigger_absolute_amount:       '',
    // actions
    action_widen_spread:                    false,
    action_widen_spread_multiplier:         '3.00',
    action_alert_broker:                    true,
    action_increase_margin:                 false,
    action_increase_margin_multiplier:      '2.00',
    // interval
    monitor_interval_ms: 500,
  });

  // ── load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [cfgRes, statsRes] = await Promise.allSettled([
          positionMonitorAPI.getConfig(),
          positionMonitorAPI.getStats(),
        ]);
        if (cfgRes.status === 'fulfilled' && cfgRes.value?.config) {
          const c = cfgRes.value.config;
          setCfg({
            trigger_pct_balance_enabled:            !!c.trigger_pct_balance_enabled,
            trigger_pct_balance_threshold:          c.trigger_pct_balance_threshold ?? '',
            trigger_x_margin_enabled:               !!c.trigger_x_margin_enabled,
            trigger_x_margin_threshold:             c.trigger_x_margin_threshold ?? '',
            trigger_absolute_enabled:               !!c.trigger_absolute_enabled,
            trigger_absolute_amount:                c.trigger_absolute_amount ?? '',
            action_widen_spread:                    !!c.action_widen_spread,
            action_widen_spread_multiplier:         c.action_widen_spread_multiplier ?? '3.00',
            action_alert_broker:                    c.action_alert_broker !== undefined ? !!c.action_alert_broker : true,
            action_increase_margin:                 !!c.action_increase_margin,
            action_increase_margin_multiplier:      c.action_increase_margin_multiplier ?? '2.00',
            monitor_interval_ms:                    c.monitor_interval_ms ?? 500,
          });
        }
        if (statsRes.status === 'fulfilled') setStats(statsRes.value?.stats || null);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setCfg(f => ({ ...f, [k]: v }));

  // ── save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true); setError(null); setSuccess(false);
    try {
      await positionMonitorAPI.saveConfig(cfg);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-10 gap-2">
      <Loader2 className="w-5 h-5 animate-spin text-[var(--accent-primary)]" />
      <span className="text-xs text-[var(--text-tertiary)]">Loading Position Monitor config…</span>
    </div>
  );

  const hasAnyTrigger = cfg.trigger_pct_balance_enabled || cfg.trigger_x_margin_enabled || cfg.trigger_absolute_enabled;
  const hasAnyAction  = cfg.action_widen_spread || cfg.action_alert_broker || cfg.action_increase_margin;

  return (
    <div className="space-y-4">

      {/* ── Stats ── */}
      {stats && (
        <div className="grid grid-cols-4 gap-2">
          <StatPill label="Triggers Today" val={stats.triggers_today  ?? '—'} color="var(--accent-danger)" />
          <StatPill label="Spread Widens"  val={stats.spread_widens   ?? '—'} color="var(--accent-warning)" />
          <StatPill label="Margin Bumps"   val={stats.margin_bumps    ?? '—'} color="var(--accent-info)" />
          <StatPill label="Alerts Sent"    val={stats.alerts_sent     ?? '—'} color="var(--accent-primary)" />
        </div>
      )}

      {/* ── Error / Success ── */}
      {error && (
        <div className="flex items-center gap-2 text-xs text-[var(--accent-danger)] bg-[var(--accent-danger)]/10 px-3 py-2 rounded-lg border border-[var(--accent-danger)]/20">
          <XCircle className="w-3.5 h-3.5 shrink-0" />
          <span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><XCircle className="w-3 h-3" /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 text-xs text-[var(--accent-success)] bg-[var(--accent-success)]/10 px-3 py-2 rounded-lg border border-[var(--accent-success)]/20">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          <span>Position Monitor configuration saved.</span>
        </div>
      )}

      {/* ────────────────── TRIGGERS ────────────────── */}
      <div>
        <p className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-tertiary)] mb-2 px-0.5">
          Profit Triggers <span className="normal-case font-normal">(any enabled trigger fires an action)</span>
        </p>
        <div className="space-y-2">

          {/* % of balance */}
          <Section
            icon={Percent}
            title="Profit as % of Balance"
            subtitle="Trigger when open profit exceeds X% of account balance"
            color="var(--accent-danger)"
            enabled={cfg.trigger_pct_balance_enabled}
            onToggle={v => set('trigger_pct_balance_enabled', v)}
            badge="Relative"
          >
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1 block">
                Threshold (% of balance)
              </label>
              <div className="flex items-center gap-2">
                <input
                  className={fieldCls}
                  type="number" min="1" step="0.01"
                  placeholder="e.g. 200 (= 200% of balance)"
                  value={cfg.trigger_pct_balance_threshold}
                  onChange={e => set('trigger_pct_balance_threshold', e.target.value)}
                />
                <span className="text-xs text-[var(--text-tertiary)] shrink-0">%</span>
              </div>
              <p className="text-[10px] text-[var(--text-tertiary)] mt-1">
                Trigger fires when <code className="text-[var(--accent-primary)]">position_profit ÷ account_balance × 100 ≥ threshold</code>.
              </p>
            </div>
          </Section>

          {/* X × margin */}
          <Section
            icon={TrendingUp}
            title="Profit as Multiple of Margin"
            subtitle="Trigger when open profit is Nx the margin used"
            color="var(--accent-warning)"
            enabled={cfg.trigger_x_margin_enabled}
            onToggle={v => set('trigger_x_margin_enabled', v)}
            badge="Relative"
          >
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1 block">
                Multiplier (× margin used)
              </label>
              <div className="flex items-center gap-2">
                <input
                  className={fieldCls}
                  type="number" min="0.1" step="0.1"
                  placeholder="e.g. 5 (= 5× margin)"
                  value={cfg.trigger_x_margin_threshold}
                  onChange={e => set('trigger_x_margin_threshold', e.target.value)}
                />
                <span className="text-xs text-[var(--text-tertiary)] shrink-0">× margin</span>
              </div>
              <p className="text-[10px] text-[var(--text-tertiary)] mt-1">
                Trigger fires when <code className="text-[var(--accent-primary)]">position_profit ≥ margin_used × multiplier</code>.
              </p>
            </div>
          </Section>

          {/* Absolute amount */}
          <Section
            icon={DollarSign}
            title="Absolute Profit Amount"
            subtitle="Trigger when a position profit exceeds a fixed dollar amount"
            color="var(--accent-info)"
            enabled={cfg.trigger_absolute_enabled}
            onToggle={v => set('trigger_absolute_enabled', v)}
            badge="Absolute"
          >
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1 block">
                Trigger amount (USD)
              </label>
              <input
                className={fieldCls}
                type="number" min="1" step="0.01"
                placeholder="e.g. 3000.00"
                value={cfg.trigger_absolute_amount}
                onChange={e => set('trigger_absolute_amount', e.target.value)}
              />
              <p className="text-[10px] text-[var(--text-tertiary)] mt-1">
                Any position with unrealised profit above this amount will trigger regardless of account size.
              </p>
            </div>
          </Section>

        </div>
      </div>

      {/* ────────────────── ACTIONS ────────────────── */}
      <div>
        <p className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-tertiary)] mb-2 px-0.5">
          Actions <span className="normal-case font-normal">(applied to the triggering position)</span>
        </p>
        <div className="space-y-2">

          <ActionRow
            icon={Layers}
            label="Widen Spread"
            sublabel="Increase the effective spread for new orders on this trader's account."
            checked={cfg.action_widen_spread}
            onChange={v => set('action_widen_spread', v)}
            color="var(--accent-warning)"
          >
            <div className="mt-2 flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] shrink-0">Multiplier</label>
              <input
                className={fieldCls}
                type="number" min="1" max="20" step="0.5"
                value={cfg.action_widen_spread_multiplier}
                onChange={e => set('action_widen_spread_multiplier', e.target.value)}
                style={{ maxWidth: '5rem' }}
              />
              <span className="text-xs text-[var(--text-tertiary)]">× normal spread</span>
            </div>
          </ActionRow>

          <ActionRow
            icon={Bell}
            label="Alert Broker Admin"
            sublabel="Send a real-time notification to the broker admin dashboard."
            checked={cfg.action_alert_broker}
            onChange={v => set('action_alert_broker', v)}
            color="var(--accent-primary)"
          />

          <ActionRow
            icon={Zap}
            label="Increase Required Margin"
            sublabel="Raise margin requirement for new orders from this trader while trigger is active."
            checked={cfg.action_increase_margin}
            onChange={v => set('action_increase_margin', v)}
            color="var(--accent-danger)"
          >
            <div className="mt-2 flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] shrink-0">Multiplier</label>
              <input
                className={fieldCls}
                type="number" min="1" max="10" step="0.5"
                value={cfg.action_increase_margin_multiplier}
                onChange={e => set('action_increase_margin_multiplier', e.target.value)}
                style={{ maxWidth: '5rem' }}
              />
              <span className="text-xs text-[var(--text-tertiary)]">× normal margin</span>
            </div>
          </ActionRow>

        </div>
      </div>

      {/* ────────────────── MONITOR CYCLE ────────────────── */}
      <div>
        <p className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-tertiary)] mb-2 px-0.5">
          Monitor Cycle
        </p>
        <div className="px-4 py-3 rounded-xl border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-2 mb-3">
            <Timer className="w-4 h-4 text-[var(--accent-primary)]" />
            <p className="text-sm font-semibold text-[var(--text-primary)]">Scan Interval</p>
          </div>
          <IntervalPicker value={cfg.monitor_interval_ms} onChange={v => set('monitor_interval_ms', v)} />
        </div>
      </div>

      {/* ────────────────── Warnings ────────────────── */}
      {hasAnyTrigger && !hasAnyAction && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-[var(--accent-warning)]/8 border border-[var(--accent-warning)]/20">
          <AlertTriangle className="w-3.5 h-3.5 text-[var(--accent-warning)] shrink-0 mt-0.5" />
          <p className="text-xs text-[var(--text-secondary)]">
            You have triggers enabled but no action configured. Select at least one action.
          </p>
        </div>
      )}

      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[var(--accent-primary)]/5 border border-[var(--accent-primary)]/15">
        <Info className="w-3.5 h-3.5 text-[var(--accent-primary)] shrink-0 mt-0.5" />
        <p className="text-[10px] text-[var(--text-secondary)] leading-relaxed">
          The bot monitors <strong>all open positions</strong> in real-time. When a trigger fires, the action applies only to
          the flagged trader until their position is closed or manually cleared.
          Actions reset automatically when the position closes.
        </p>
      </div>

      {/* ────────────────── Save ────────────────── */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={saving || (hasAnyTrigger && !hasAnyAction)}
          className="flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
          style={{ backgroundColor: 'var(--accent-primary)', color: '#fff' }}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? 'Saving…' : 'Save Position Monitor Config'}
        </button>
      </div>

    </div>
  );
}